import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { upcomingAppointments } from "services/consultations";
import { getLoggedInUser, getUserData } from "utils/storageUtils";
import { GET_DOCTOR_APPOINTMENTS } from "api/dataProviderTypes";
import { AppointmentRequest } from "api/types";
import DashboardWrapper from "components/Layout/DashboardWrapper";
import CalendarView from "components/Calendar/CalendarView";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { routePaths } from "constants/roles";
import { canChooseDoctor } from "utils/availability";

const storageUser = getUserData();

type ViewMode = "list" | "calendar";

const FRENCH_DAYS = [
  "Dimanche",
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
];

const FRENCH_MONTHS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

const formatDate = (inputDate: string) => {
  const date = new Date(inputDate);
  return `${FRENCH_DAYS[date.getDay()]} ${date.getDate()} ${
    FRENCH_MONTHS[date.getMonth()]
  } ${date.getFullYear()}`;
};

const isToday = (dateString?: string) => {
  if (!dateString) return false;
  const today = new Date();
  const date = new Date(dateString);
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isAICreated = (appointment: AppointmentRequest) => {
  const description = appointment?.description || "";
  return (
    description.includes("RDV réservé via l'IA") ||
    description.includes("RDV synchronisé depuis Google Calendar") ||
    description.includes("via l'IA")
  );
};

const initialsOf = (firstName?: string, lastName?: string) => {
  const f = (firstName || "").trim().charAt(0);
  const l = (lastName || "").trim().charAt(0);
  return `${f}${l}`.toUpperCase();
};

const Appointments = () => {
  /** Secrétaire et admin consultent la disponibilité de chaque médecin ; un médecin, la sienne dans son Profil. */
  const showsAllDoctors = canChooseDoctor(getLoggedInUser());
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const { isLoading, data, isFetching } = useQuery(
    GET_DOCTOR_APPOINTMENTS,
    () => upcomingAppointments(storageUser?.user?.id || 123)
  );

  const appointments: AppointmentRequest[] = data?.data || [];

  const todayCount = useMemo(
    () => appointments.filter((a) => isToday(a?.appointmentDate)).length,
    [appointments]
  );

  const aiCount = useMemo(
    () => appointments.filter(isAICreated).length,
    [appointments]
  );

  return (
    <DashboardWrapper showSpinner={isLoading || isFetching}>
      <>
        <div className="space-y-8">
          {/* Hero */}
          <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-100 rounded-[28px] border border-slate-200 bg-white shadow-sm p-6 md:p-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="space-y-3">
                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  <span className="relative inline-flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
                  </span>
                  Agenda du jour
                </span>
                <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
                  Rendez-vous
                </h1>
                <p className="text-sm text-slate-500 max-w-xl">
                  Pilotez votre planning, repérez les créneaux du jour et
                  identifiez instantanément les RDV pris par l&apos;assistant
                  IA.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to={showsAllDoctors ? routePaths.AVAILABILITY : routePaths.PROFILE}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-emerald-700 transition-colors duration-200 hover:bg-emerald-50 hover:border-emerald-200"
                >
                  <i className="bx bx-time-five text-base" aria-hidden />
                  {showsAllDoctors ? "Disponibilités des médecins" : "Mes disponibilités"}
                </Link>

                {/* View toggle */}
                <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50/70 p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300",
                      viewMode === "list"
                        ? "bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-100"
                        : "text-slate-500 hover:text-slate-800"
                    )}
                  >
                    <i className="bx bx-list-ul text-base"></i>
                    Liste
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("calendar")}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300",
                      viewMode === "calendar"
                        ? "bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-100"
                        : "text-slate-500 hover:text-slate-800"
                    )}
                  >
                    <i className="bx bx-calendar-event text-base"></i>
                    Calendrier
                  </button>
                </div>
              </div>
            </div>

            {/* Stats strip */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <StatCard
                icon="bx-calendar-check"
                label="À venir"
                value={appointments.length}
                tone="slate"
              />
              <StatCard
                icon="bx-time-five"
                label="Aujourd'hui"
                value={todayCount}
                tone="green"
              />
              <StatCard
                icon="bx-bot"
                label="Pris par l'IA"
                value={aiCount}
                tone="emerald"
              />
            </div>
          </section>

          {/* Body */}
          {viewMode === "calendar" ? (
            <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-200">
              <CalendarView appointments={appointments} />
            </section>
          ) : (
            <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-200 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Heure</th>
                      <th className="px-4 py-3 text-left">Objet</th>
                      <th className="px-4 py-3 text-left">Patient</th>
                      <th className="px-4 py-3 text-left">Contact</th>
                      <th className="px-4 py-3 text-right pr-6">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-16">
                          <div className="flex flex-col items-center justify-center text-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 mb-3">
                              <i className="bx bx-calendar text-2xl"></i>
                            </div>
                            <p className="text-sm font-medium text-slate-700">
                              Aucun rendez-vous à venir
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Les nouveaux RDV s&apos;afficheront ici dès leur
                              création.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}

                    {appointments.map((appointment, idx) => {
                      const aiCreated = isAICreated(appointment);
                      const today = isToday(appointment?.appointmentDate);
                      return (
                        <tr
                          key={idx}
                          className={cn(
                            "group/row border-t border-slate-100 transition-colors",
                            today
                              ? "bg-green-50 hover:bg-green-100/60"
                              : "hover:bg-emerald-50/40",
                            aiCreated && !today && "border-l-2 border-l-emerald-400"
                          )}
                        >
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-800 font-medium">
                                {appointment?.appointmentDate
                                  ? formatDate(appointment.appointmentDate)
                                  : "—"}
                              </span>
                              {today && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-700">
                                  Aujourd&apos;hui
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700 font-medium">
                            {appointment?.appointmentTime || "—"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {aiCreated && (
                                <span
                                  title="Créé par l'IA"
                                  className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700"
                                >
                                  <i className="bx bx-bot text-xs"></i>
                                  IA
                                </span>
                              )}
                              <span className="text-sm text-slate-700">
                                {appointment?.appointmentObject || "—"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-xs font-semibold text-white ring-2 ring-white shadow-sm">
                                {initialsOf(
                                  appointment?.patient?.firstName,
                                  appointment?.patient?.lastName
                                ) || (
                                  <i className="bx bx-user text-sm"></i>
                                )}
                              </div>
                              <div className="leading-tight">
                                <div className="text-sm font-medium text-slate-800">
                                  {appointment?.patient?.firstName || ""}{" "}
                                  {appointment?.patient?.lastName || ""}
                                </div>
                                <div className="text-xs text-slate-400">
                                  {appointment?.patient?.phoneNumber || "—"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500">
                            {appointment?.patient?.email || "—"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1 opacity-70 group-hover/row:opacity-100 transition-opacity pr-2">
                              <button
                                type="button"
                                title="Détails"
                                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                              >
                                <i className="bx bx-show text-base"></i>
                              </button>
                              <button
                                type="button"
                                title="Ajouter une note"
                                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                              >
                                <i className="bx bx-list-plus text-base"></i>
                              </button>
                              <button
                                type="button"
                                title="Annuler"
                                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                              >
                                <i className="bx bx-trash text-base"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </>
    </DashboardWrapper>
  );
};

const toneClassMap: Record<string, string> = {
  slate: "from-slate-50 to-white border-slate-200 text-slate-600",
  green: "from-green-50 to-white border-green-200 text-green-700",
  emerald: "from-emerald-50 to-white border-emerald-100 text-emerald-700",
};

const StatCard: React.FC<{
  icon: string;
  label: string;
  value: number;
  tone: "slate" | "green" | "emerald";
}> = ({ icon, label, value, tone }) => (
  <div
    className={cn(
      "rounded-xl border bg-gradient-to-br p-4 flex items-center gap-3",
      toneClassMap[tone]
    )}
  >
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-100">
      <i className={cn("bx text-xl", icon)}></i>
    </div>
    <div className="leading-tight">
      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">
        {label}
      </div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
    </div>
  </div>
);

export default Appointments;
